import React, { useEffect, useState } from 'react';
import Dashboard from './pages/Dashboard';
import FlockList from './pages/FlockList';
import SheepProfile from './pages/SheepProfile';
import AuthPage from './pages/AuthPage';
import Settings from './pages/Settings';
import Finance from './pages/Finance';
import SheepSearch from './components/SheepSearch';
import { api } from './api';
import { onCurrencyChange } from './utils/currency';

const ICONS = {
  dashboard: <><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></>,
  flock: <><path d="M7.5 17.5c-2.8 0-5-2.1-5-4.8 0-2.1 1.4-3.9 3.4-4.5a5.1 5.1 0 0 1 9.6-1.1c3.1-.2 5.5 2 5.5 4.8 0 2.5-2 4.6-4.7 4.6H7.5Z"/><path d="M8 17.5V21M16 17v4M18.5 8.5 21 6.8M6 8.3 3.5 6.8"/></>,
  finance: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></>,
  settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></>,
};

const NAV = [
  { key: 'dashboard', label: 'Overview', caption: 'Farm pulse' },
  { key: 'flock', label: 'Flock', caption: 'Animals & records' },
  { key: 'finance', label: 'Finance', caption: 'Costs & revenue' },
  { key: 'settings', label: 'Settings', caption: 'Data & account' },
];

function Icon({ name }) {
  return <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[name]}</svg>;
}

export default function App() {
  const [auth, setAuth] = useState({ loading: true, configured: false, authenticated: false, user: null });
  const [page, setPage] = useState('dashboard');
  const [selectedId, setSelectedId] = useState(null);
  const [, setCurrencyTick] = useState(0);

  useEffect(() => onCurrencyChange(() => setCurrencyTick(t => t + 1)), []);
  const refreshAuth = () => api.authStatus().then(setAuth).catch(() => setAuth({ loading: false, configured: false, authenticated: false, user: null }));
  useEffect(() => { refreshAuth(); }, []);

  const navigate = (nextPage, id) => {
    setPage(nextPage);
    if (id !== undefined) setSelectedId(id);
    document.querySelector('.main-content')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (auth.loading) return <div className="loading-screen"><span className="loader" /><p>Preparing your farm workspace</p></div>;
  if (!auth.configured) return <AuthPage mode="setup" onAuthed={refreshAuth} />;
  if (!auth.authenticated) return <AuthPage mode="login" onAuthed={refreshAuth} />;

  const activeNav = page === 'profile' ? 'flock' : page;
  const current = NAV.find(item => item.key === activeNav) || NAV[0];
  const initials = (auth.user?.username || 'Farm manager').slice(0, 2).toUpperCase();

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <button className="sidebar-logo" type="button" onClick={() => navigate('dashboard')}>
          <span className="brand-mark" aria-hidden="true">S</span>
          <span><strong>Flockwise</strong><small>Sheep management</small></span>
        </button>
        <nav className="sidebar-nav" aria-label="Main navigation">
          <p className="nav-label">Workspace</p>
          {NAV.map(item => (
            <button type="button" key={item.key} className={`nav-item ${activeNav === item.key ? 'active' : ''}`} onClick={() => navigate(item.key)} aria-current={activeNav === item.key ? 'page' : undefined}>
              <Icon name={item.key} />
              <span className="nav-copy"><strong>{item.label}</strong><small>{item.caption}</small></span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="user-avatar">{initials}</div>
          <div><strong>{auth.user?.username || 'Farm manager'}</strong><span>Administrator</span></div>
        </div>
      </aside>
      <main className="main-content">
        <header className="app-topbar">
          <div className="topbar-context"><span>Workspace</span><strong>{current.label}</strong></div>
          <SheepSearch onSelect={(sheep) => navigate('profile', sheep.id)} placeholder="Find an animal by tag or breed…" />
          <button className="topbar-avatar" type="button" onClick={() => navigate('settings')} aria-label="Open account settings">{initials}</button>
        </header>
        {page === 'dashboard' && <Dashboard onNavigate={navigate} />}
        {page === 'flock' && <FlockList onNavigate={navigate} />}
        {page === 'profile' && selectedId && <SheepProfile sheepId={selectedId} onNavigate={navigate} />}
        {page === 'finance' && <Finance />}
        {page === 'settings' && <Settings user={auth.user} onLogout={() => setAuth(a => ({ ...a, authenticated: false, user: null }))} />}
      </main>
    </div>
  );
}
